<template>
  <div id="wifi-overrides" ref="wifiOverrides">
    <v-row>
      <v-col class="custom-card px-0 pa-0 ">
        <!-- MOBILE: search + botón de acciones -->
        <v-row class="mb-3 mt-2 mx-0 d-flex d-sm-none">
          <v-col cols="12" class="pa-0">
            <div class="mobile-toolbar-wrapper">
              <search-bar
                :cols="12"
                class="mobile-search-bar"
                @searchText="onSearch"
                :placeholder="$t('wifiOverrides.filterBssid')"
                notEncodedText="true"
              />
              <v-menu offset-y left>
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    color="primary"
                    class="mobile-action-btn"
                    elevation="1"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>

                <v-list dense>
                  <v-list-item @click="openCreate">
                    <v-list-item-icon><v-icon>mdi-plus</v-icon></v-list-item-icon>
                    <v-list-item-title>{{ $t('wifiOverrides.add') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="showBulk = true">
                    <v-list-item-icon><v-icon>mdi-upload</v-icon></v-list-item-icon>
                    <v-list-item-title>{{ $t('wifiOverrides.bulkUpload') }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </v-col>
        </v-row>

        <!-- DESKTOP/TABLET -->
        <v-row class="mb-3 mt-2 align-center mx-0 d-none d-sm-flex">
          <v-col cols="12" class="d-flex align-center pa-0">
            <div class="search-desktop">
              <search-bar
                @searchText="onSearch"
                :placeholder="$t('wifiOverrides.filterBssid')"
                notEncodedText="true"
              />
            </div>
            <div class="actions-desktop">
              <v-btn
                id="add-override"
                height="40"
                depressed
                @click="openCreate"
                class="font-weight-bold"
                color="primary"
                dark
              >
                <v-icon left>mdi-plus</v-icon>{{ $t('wifiOverrides.add') }}
              </v-btn>
              <v-btn
                id="bulk-upload"
                height="40"
                depressed
                @click="showBulk = true"
                class="font-weight-bold mr-3"
                color="secondary"
                dark
              >
                <v-icon left>mdi-upload</v-icon>{{ $t('wifiOverrides.bulkUpload') }}
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <DataTable
          :settings="settings"
          :page="pagination.page"
          :pageSize="pagination.size"
          name="wifiOverrides"
          :resizeRef="$refs.wifiOverrides"
          :columns="columnDefs"
          :rows="filteredRows"
          :totalRows="total"
          :maxHeight="'calc(100vh - 280px)'"
          :customTableContainerClass="'wifi-overrides-page'"
          @onExport="downloadCsv"
          @onPaginationChange="onPaginationChange"
        />

        <wifi-overrides-bulk-modal
          v-model="showBulk"
          :org-id="currentOrgId"
          @refresh="fetchOverrides"
        />

        <add-wifi-override-modal
          v-if="showAdd"
          :key="addMode + (selectedRow ? selectedRow.bssid : 'new')"
          v-model="showAdd"
          :mode="addMode"
          :org-id="currentOrgId"
          :model="selectedRow"
          :mapbox-token="mapboxToken"
          @refresh="fetchList"
        />

        <v-dialog v-model="dialogs.delete.open" max-width="290">
          <v-card>
            <v-card-title class="headline">{{ $t('wifiOverrides.delete') }}</v-card-title>
            <v-card-text class="mt-3">
              {{ $t('wifiOverrides.deleteConfirm') }}
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn height="32" text color="secondary" @click="dialogs.delete.open = false" class="primary--text font-weight-bold">
                {{ $t('wifiOverrides.cancel') }}
              </v-btn>
              <v-btn height="32" depressed color="secondary" :loading="saving" :disabled="saving" @click="doDelete" class="px-8 float-right">
                {{ $t('yes') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SearchBar from '@/components/search-bar/search-bar';
import DataTable from '@/components/shared/data-table/data-table.vue';
import { canonicalizeBssid } from '@/services/wifiOverrides';
import WifiOverridesBulkModal from '@/components/configurations/WifiOverridesBulkModal.vue';
import AddWifiOverrideModal from '@/components/configurations/AddWifiOverrideModal.vue';
import { csvMixin } from '@/mixins/csvExport';

export default {
  name: 'WifiOverridesPage',
  components: { DataTable, 'search-bar': SearchBar, WifiOverridesBulkModal, AddWifiOverrideModal },
  mixins: [csvMixin],
  data() {
    return {
      columnDefs: null,
      settings: {
        noPagination: true,          
        actionsPanel: true,
        moreActions: [],             
      },
      searchValue: '',
      dialogs: {
        single: { open: false, isEdit: false },
        bulk: { open: false },
        delete: { open: false, target: null },
      },
      singleModel: { bssid: '', lat: null, lng: null },
      singleValid: false,
      bulk: { csv: '', dryRun: true, loading: false, result: null },
      saving: false,
      rules: {
        bssid: (v) => !!canonicalizeBssid(v) || this.$t('wifiOverrides.validation.bssid'),
        lat: (v) => (typeof v === 'number' && v >= -90 && v <= 90) || this.$t('wifiOverrides.validation.lat'),
        lng: (v) => (typeof v === 'number' && v >= -180 && v <= 180) || this.$t('wifiOverrides.validation.lng'),
      },
      showBulk: false,
      showAdd: false,
      currentOrgId: '',
      addMode: 'create',
      selectedRow: null,
      guideUrl: 'https://knowledge.link-labs.com/LinkLabsKowledgeBase/Features/How+to+Use+the+Wifi+Override+Feature',
    };
  },
  computed: {
    ...mapGetters('wifiOverrides', ['overrides', 'loading', 'total', 'pagination']),
    ...mapGetters('organization', ['currentOrganization']),

    filteredRows() {
      if (!this.searchValue) return this.overrides;
      const q = this.searchValue.toLowerCase().replace(/\s+/g, '');
      return this.overrides.filter(r => (r.bssid || '').toLowerCase().includes(q));
    },

    bulkTotal() {
      const r = this.bulk.result;
      return r?.total ?? ((r?.inserted || 0) + (r?.updated || 0) + (r?.skipped || 0) + (r?.invalid || 0));
    },
    
    mapboxToken() {
      return process.env.VUE_APP_MAPBOX_ACCESS_TOKEN || '';
    }
  },
  watch: {
    currentOrganization() {
      this.fetchList();
    },
  },
  beforeMount() {
    this.columnDefs = [
      { name: this.$t('wifiOverrides.bssid'), field: 'bssid', type: 'data', width: '20%' },
      { name: this.$t('wifiOverrides.lat'), field: 'lat', type: 'data' },
      { name: this.$t('wifiOverrides.lng'), field: 'lng', type: 'data' },
      { name: this.$t('wifiOverrides.updatedAt'), field: 'updatedAt', type: 'date', format: 'MM/DD/YYYY h:mm A' },
    ];
  },
  mounted() {
    this.setSettingsMoreActions();
    this.fetchList();
  },
  methods: {
    ...mapActions('wifiOverrides', ['fetchOverrides', 'updateFilters', 'bulkUpload', 'createOverride', 'updateOverride', 'deleteOverride']),

    setSettingsMoreActions() {
      this.settings.moreActions = [
        { name: this.$t('table-edit'), fn: this.onEditAction, icon: 'pen' },
        { name: this.$t('table-delete'), fn: this.onDeleteAction, icon: 'delete' },
      ];
    },

    async fetchList() {
      try {
        await this.fetchOverrides({ fetchAll: true });
      } catch (e) {
        this.$toasted.show(e.message || this.$t('something-went-wrong'), {
          position: 'bottom-right',
          className: ['toast-error'],
          duration: 2500,
        });
      }
    },

    onSearch(str) {
      this.searchValue = (str || '').trim();
      this.updateFilters({ bssid: this.searchValue }); 
    },

    onEditAction(row) { this.openEdit(row); },
    onDeleteAction(row) { this.confirmDelete(row); },

    openCreate() {
      this.addMode = 'create';
      this.selectedRow = { bssid: '', lat: null, lng: null };
      this.showAdd = true;
    },
    openEdit(item) {
      this.addMode = 'edit';
      this.selectedRow = { bssid: item.bssid, lat: item.lat, lng: item.lng };
      this.showAdd = true;
    },
    closeSingle() {
      this.showAdd = false;
    },
    normalizeBssid() {
      if (this.selectedRow.bssid) {
        this.selectedRow.bssid = canonicalizeBssid(this.selectedRow.bssid);
      }
    },

    confirmDelete(item) {
      this.dialogs.delete = { open: true, target: item };
    },
    async doDelete() {
      if (!this.dialogs.delete.target) return;
      this.saving = true;
      try {
        await this.deleteOverride({ bssid: this.dialogs.delete.target.bssid });
        this.$toasted.show(this.$t('wifiOverrides.snackbar.deleted'), { position: 'bottom-right', className: ['toast-success'], duration: 2500 });
        this.dialogs.delete.open = false;
        this.fetchList();
      } catch (e) {
        this.$toasted.show(e.message || this.$t('something-went-wrong'), { position: 'bottom-right', className: ['toast-error'], duration: 2500 });
      } finally {
        this.saving = false;
      }
    },    

    openBulk() {
      this.dialogs.bulk.open = true;
      this.bulk = { csv: '', dryRun: true, loading: false, result: null };
    },
    closeBulk() { this.dialogs.bulk.open = false; },
    async runBulk() {
      if (!this.bulk.csv || this.bulk.loading) return;
      this.bulk.loading = true;
      try {
        const result = await this.bulkUpload({ fileText: this.bulk.csv, dryRun: this.bulk.dryRun });
        this.bulk.result = result;
        this.$toasted.show(
          this.bulk.dryRun ? this.$t('wifiOverrides.snackbar.bulkValidated') : this.$t('wifiOverrides.snackbar.bulkDone'),
          { position: 'bottom-right', className: ['toast-success'], duration: 1500 }
        );
        if (!this.bulk.dryRun) this.fetchList();
      } catch (e) {
        this.$toasted.show(e.message || this.$t('something-went-wrong'), { position: 'bottom-right', className: ['toast-error'], duration: 2500 });
      } finally {
        this.bulk.loading = false;
      }
    },
   
    async onPaginationChange({ page, pageSize }) {
      await this.$store.dispatch('wifiOverrides/updatePagination', { page, size: pageSize });
      await this.fetchOverrides();
    },

    downloadCsv(rows) {
      if (!rows || rows.length === 0) {
        this.$toasted.show(
          'No rows selected for export',
          { position: 'bottom-right', className: ['toast-warning'], duration: 2000 }
        );
        return;
      }
      
      // Normalize data for CSV export
      const normalizedData = rows.map(row => ({
        bssid: row.bssid || '',
        lat: row.lat || '',
        lng: row.lng || ''
      }));

      const orgIdRaw = this.currentOrganization?.id || 'org';
      const orgId = String(orgIdRaw).replace(/[^a-zA-Z0-9_-]/g, '');

      const iso = new Date().toISOString(); // YYYY-MM-DDTHH:mm:ss.sssZ
      const date = iso.slice(0,10);         // YYYY-MM-DD
      const time = iso.slice(11,16).replace(':',''); // HHmm
      const filename = `wifi-overrides_${orgId}_${date}_${time}Z`;
      
      // Use csvMixin for export
      this.csvExport(normalizedData, filename);
      
      this.$toasted.show(
        `Exported ${rows.length} override(s)`,
        { position: 'bottom-right', className: ['toast-success'], duration: 2000 }
      );
    },

    openFile(file) {
      window.open(file, '_blank');
    },
  },
};
</script>

<style lang="scss" scoped>
.right-btn-container { 
  height: 100%; 
  float: right; 
}

.headline { 
  height: 48px; 
  font-size: 18px !important; 
  font-weight: 600; 
  color: #3B5762; 
}

// MOBILE STYLES
.mobile-toolbar-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  width: 100%;
}

.mobile-search-bar {
  flex: 1 1 auto;
  min-width: 0;
  
  // Override interno del SearchBar para que no tenga margin
  ::v-deep .v-col {
    padding: 0 !important;
    margin: 0 !important;
  }
  
  ::v-deep .v-text-field {
    width: 100%;
    margin: 0 !important;
  }
  
  ::v-deep .v-input {
    width: 100%;
  }
  
  ::v-deep .v-input__control {
    min-height: 44px !important;
  }
  
  ::v-deep .v-input__slot {
    min-height: 44px !important;
    margin-bottom: 0 !important;
  }
}

.mobile-action-btn {
  width: 44px !important;
  height: 44px !important;
  min-width: 44px !important;
  flex-shrink: 0;
  border-radius: 8px;
}

// DESKTOP STYLES
.search-desktop {
  flex: 1 1 auto;
  min-width: 320px;
  margin-left: 12px;
  margin-right: 12px;
}

.actions-desktop {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

::v-deep .wifi-overrides-page.tableContainer {
  overflow-x: hidden !important;
  overflow-y: auto !important;
}

::v-deep .wifi-overrides-page table {
  width: 100% !important;
  table-layout: fixed !important;
}

::v-deep .wifi-overrides-page.tableContainer {
  max-height: calc(100vh - 235px - 2px) !important;
  overflow-y: auto !important;
}

// RESPONSIVE
@media (max-width: 599px) {
  .wifi-overrides-page {
    margin-left: 0 !important;
  }
  
  .mobile-toolbar-wrapper {
    padding: 0 12px;
  }
  
  // Forzar estilos del search en mobile
  .mobile-search-bar {
    ::v-deep .v-input__slot {
      min-height: 44px !important;
    }

    ::v-deep .v-text-field__slot input {
      font-size: 14px !important;
    }
  }
}

@media (max-width: 812px) { 
  .right-btn-container { 
    float: none !important; 
    margin-left: 13px; 
  } 
}

@media all and (-ms-high-contrast:none) { 
  .right-btn-container { 
    padding-top: 2px; 
  } 
}
</style>